import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { ErrorMessage } from '@components/core-ui/ErrorMessage';
import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { CheckBox } from '@components/core-ui/form-elements/CheckBox';
import { Input } from '@components/core-ui/form-elements/Input';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { createFetchAdminJobQueryFn } from '@pages/Admin/JobDetailsPage/api/fetchAdminJob';
import { useAPI } from '@providers/useAPI';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminService } from 'app/API';
import { notifyApiCallError } from 'app/http/notifyApiCallError';
import { adminJobsKeys } from 'app/queries/adminJobs/adminJobsKeys';
import { createFetchAdminJobTransactionsQueryFn } from 'app/queries/adminJobs/createFetchAdminJobTransactionsQueryFn';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { CellContent } from './AdminRefundJobModal/styles/CellContent';
import { Content } from './AdminRefundJobModal/styles/Content';

type TProps = {
  onCancel: () => void;
  onSuccess: () => void;
  id: string;
};

export const AdminRefundJobModal = ({ onCancel, onSuccess, id }: TProps) => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const [editedShouldLockJob, setShouldLockJob] = useState<boolean>();
  const [price, setPrice] = useState(0);
  const [reason, setReason] = useState('');
  const [transactionIds, setTransactionIds] = useState<string[]>([]);

  const jobQuery = useQuery({
    queryKey: adminJobsKeys.detail(id),
    queryFn: createFetchAdminJobQueryFn(call),
  });
  const transactionsQuery = useQuery({
    queryKey: adminJobsKeys.transactionsByJob(id),
    queryFn: createFetchAdminJobTransactionsQueryFn(call),
  });
  const job = jobQuery.data;
  const jobTitle = job ? job.name || 'No title' : 'Loading...';
  const isASOneJob = Boolean(job?.meetingId);
  const shouldLockJob = editedShouldLockJob ?? isASOneJob;
  const transactions = transactionsQuery.data ?? [];

  const refundMutation = useMutation({
    mutationFn: () =>
      call(() => AdminService.refundJob({ jobId: id, requestBody: { reason, transactionIds, shouldLockJob } })),
    onSuccess: (result) => {
      if (result?.error) {
        toast.error(result.message ?? result.error);
        return;
      }
      onSuccess();
    },
    onError: notifyApiCallError,
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: adminJobsKeys.detail(id) }),
        queryClient.invalidateQueries({ queryKey: adminJobsKeys.transactionsByJob(id) }),
      ]),
  });

  const isLoading = jobQuery.isLoading || transactionsQuery.isLoading || refundMutation.isPending;
  const isLoaded = jobQuery.isSuccess && transactionsQuery.isSuccess;
  const isSubmitDisabled = !isLoaded || !reason.length || !transactionIds.length || !price || refundMutation.isPending;

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitDisabled) {
      return;
    }
    refundMutation.mutate();
  };

  return (
    <ModalWrapper width="500px">
      {isLoading ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Refund Job</ModalTitle>
      <Content>
        <div className="content">
          <div className="input-wrapper">
            <div className="label">Job Title</div>
            <p>{jobTitle || 'Unnamed'}</p>
          </div>

          <div className="input-wrapper">
            <div className="label">Amount</div>
            <p>${(price / 100).toFixed(2)}</p>
          </div>

          <div className="input-wrapper reason">
            <div className="label">Reason</div>
            <Input
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReason(e.target.value)}
              label="Type the reason of refund"
              required={true}
              value={reason}
            />
          </div>

          <TableWrapper className="custom-table">
            <Table>
              <Thead $white>
                <Tr>
                  <Th></Th>
                  <Th>Code</Th>
                  <Th>Date</Th>
                  <Th>Amount</Th>
                  <Th>Type</Th>
                </Tr>
              </Thead>
              <Tbody>
                {(transactions || []).map((data) => (
                  <Tr key={data.id}>
                    <Td>
                      <CellContent>
                        {data.amount < 0 ? (
                          <CheckBox
                            name={`transaction-${data.id}`}
                            checked={transactionIds.includes(data.id)}
                            onChange={(v) => {
                              if (v) {
                                setTransactionIds([...transactionIds, data.id]);
                                setPrice(price + data.amount * -1);
                              } else {
                                setTransactionIds(transactionIds.filter((id) => id !== data.id));
                                setPrice(price - data.amount * -1);
                              }
                            }}
                          />
                        ) : null}
                      </CellContent>
                    </Td>
                    <Td>
                      <CellContent>{data.codeId}</CellContent>
                    </Td>
                    <Td>
                      <CellContent>{getAmericanTime(data.createdAt)}</CellContent>
                    </Td>
                    <Td>
                      <CellContent>{Math.abs(data.amount / 100).toFixed(2)}</CellContent>
                    </Td>
                    <Td>
                      <CellContent>{data.amount < 0 ? 'Charge' : 'Refund'}</CellContent>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableWrapper>

          {!isLoading && !transactions.length ? <p className="description">No transactions found</p> : null}

          {jobQuery.isError || transactionsQuery.isError ? (
            <ErrorMessage error="Failed to load the job transactions. Please try again." />
          ) : null}

          {isASOneJob ? (
            <div className="input-wrapper checkbox-wrapper">
              <CheckBox name="lock-after-refund" checked={shouldLockJob} onChange={(v) => setShouldLockJob(v)} />
              <label>Lock the job after refunding</label>
            </div>
          ) : null}
        </div>
        <form onSubmit={(e) => submit(e)}>
          <div className="buttonContainer">
            <OutlinedButton type="button" onClick={onCancel}>
              Cancel
            </OutlinedButton>
            <Button type="submit" disabled={isSubmitDisabled}>
              Refund
            </Button>
          </div>
        </form>
      </Content>
    </ModalWrapper>
  );
};
